/* ==========================================================================
   <reviews-marquee>  Customer reviews row.
   Desktop (fine pointer): continuous sub-pixel scroll (transform), paused on hover/focus.
   Touch: native swipeable row with snap; advances one card every few seconds and
   resumes a moment after the visitor lets go.
   The list is rendered twice so the loop is seamless; the copy is aria-hidden.
   No movement with prefers-reduced-motion, while the tab is hidden, or off screen.
   ========================================================================== */
if (!customElements.get('reviews-marquee')) {
  customElements.define(
    'reviews-marquee',
    class ReviewsMarquee extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.reviews-marquee__viewport');
        this.track = this.querySelector('.reviews-marquee__track');
        this.items = [...this.querySelectorAll('.reviews-marquee__item')];
        if (!this.viewport || !this.track || this.items.length < 2) return;

        this.speed = Number(this.dataset.speed) || 36; // px per second (desktop)
        this.interval = Number(this.dataset.interval) || 4000; // ms per card (touch)
        this.rtl = getComputedStyle(this).direction === 'rtl';
        this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.position = 0;
        this.paused = false;
        this.visible = false;

        const pause = () => { this.paused = true; };
        const resume = () => { this.paused = false; };
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.viewport.addEventListener('touchstart', () => this.holdTouch(), { passive: true });
        this.viewport.addEventListener('touchend', () => this.releaseTouch(), { passive: true });
        this.viewport.addEventListener('touchcancel', () => this.releaseTouch(), { passive: true });
        // Re-centre into the middle of the loop once scrolling has fully settled (never during a swipe)
        if ('onscrollend' in window) {
          this.viewport.addEventListener('scrollend', () => this.recentre());
        } else {
          this.viewport.addEventListener('scroll', () => {
            clearTimeout(this.settleTimer);
            this.settleTimer = setTimeout(() => this.recentre(), 180);
          }, { passive: true });
        }

        this.resizeObserver = new ResizeObserver(() => { this.loop = undefined; });
        this.resizeObserver.observe(this);
        this.fine.addEventListener('change', () => this.restart());

        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.restart();
        });
        this.observer.observe(this);
        this.onVisibility = () => this.restart();
        document.addEventListener('visibilitychange', this.onVisibility);
        this.setAttribute('data-ready', '');
      }

      disconnectedCallback() {
        this.stop();
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      /** Width of one full set of cards: the distance from a card to its twin. */
      get loopWidth() {
        if (!this.loop) {
          const half = this.items.length / 2;
          this.loop = Math.abs(this.items[half].offsetLeft - this.items[0].offsetLeft);
        }
        return this.loop;
      }

      restart() {
        this.stop();
        const touch = !this.fine.matches;
        this.toggleAttribute('data-touch', touch);
        if (touch) {
          this.track.style.transform = '';
          // Start in the middle so the visitor can swipe both ways from the first card
          if (!this.centred) {
            this.setScroll(this.loopWidth, false);
            this.centred = true;
          }
        }
        if (!this.visible || document.hidden || this.reduced.matches) return;
        touch ? this.startTouch() : this.startDesktop();
      }

      stop() {
        cancelAnimationFrame(this.frame);
        clearInterval(this.timer);
        this.last = undefined;
      }

      /* ---------- Desktop: continuous movement ---------- */
      startDesktop() {
        const step = (time) => {
          if (this.last !== undefined && !this.paused) {
            const delta = Math.min(time - this.last, 50) / 1000;
            this.position = (this.position + this.speed * delta) % this.loopWidth;
            // RTL content travels to the right, LTR to the left
            const x = this.rtl ? this.position : -this.position;
            this.track.style.transform = `translate3d(${x}px, 0, 0)`;
          }
          this.last = time;
          this.frame = requestAnimationFrame(step);
        };
        this.frame = requestAnimationFrame(step);
      }

      /* ---------- Touch: one card at a time ---------- */
      get scrollOffset() {
        return Math.abs(this.viewport.scrollLeft);
      }

      setScroll(value, smooth) {
        const left = this.rtl ? -value : value;
        if (smooth) this.viewport.scrollTo({ left, behavior: 'smooth' });
        else this.viewport.scrollLeft = left;
      }

      startTouch() {
        this.timer = setInterval(() => {
          if (this.paused) return;
          const step = Math.abs(this.items[1].offsetLeft - this.items[0].offsetLeft);
          this.setScroll(this.scrollOffset + step, true);
        }, this.interval);
      }

      /** Keep the position between half a loop and one and a half loops: both directions stay open. */
      recentre() {
        if (!this.hasAttribute('data-touch')) return;
        const loop = this.loopWidth;
        const offset = this.scrollOffset;
        if (offset < loop * 0.5) this.setScroll(offset + loop, false);
        else if (offset > loop * 1.5) this.setScroll(offset - loop, false);
      }

      holdTouch() {
        this.paused = true;
        clearTimeout(this.resumeTimer);
      }

      releaseTouch() {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => { this.paused = false; }, 3000);
      }
    }
  );
}
